import { HazardType } from "generated-graphql/graphql";
import { isEqual, sortBy } from "lodash-es";

import { ChemicalDetailInputs } from ".";

type StoredHazardCategory = { code: string; hazardType: HazardType };

type HazardCategoryCodeFields = Pick<
  ChemicalDetailInputs,
  "healthHazardCategoryCodes" | "physicalHazardCategoryCodes"
>;

/**
 * Form values for the two hazard pickers. Undefined when the pickers are not
 * shown, so the save input omits the list and the server leaves hazard rows alone.
 */
export function seedHazardCategoryCodes(
  chemical: { hazardCategories?: readonly StoredHazardCategory[] | null } | undefined,
  usesHazardCategories: boolean
): HazardCategoryCodeFields {
  if (!usesHazardCategories) {
    return { healthHazardCategoryCodes: undefined, physicalHazardCategoryCodes: undefined };
  }
  const codesOf = (hazardType: HazardType) =>
    (chemical?.hazardCategories ?? [])
      .filter((category) => category.hazardType === hazardType)
      .map((category) => category.code);
  return {
    healthHazardCategoryCodes: codesOf(HazardType.Health),
    physicalHazardCategoryCodes: codesOf(HazardType.Physical),
  };
}

const sameCodes = (a: readonly string[] | undefined, b: readonly string[] | undefined) =>
  isEqual(sortBy(a ?? []), sortBy(b ?? []));

/**
 * Drops both picker fields when neither selection differs from the seeded
 * values, so the save input omits `hazardCategoryCodes` and an unrelated edit
 * cannot rewrite the chemical's hazards.
 */
export function omitUntouchedHazardCategoryCodes<T extends HazardCategoryCodeFields>(
  values: T,
  seed: HazardCategoryCodeFields | undefined
): T {
  const untouched =
    sameCodes(values.healthHazardCategoryCodes, seed?.healthHazardCategoryCodes) &&
    sameCodes(values.physicalHazardCategoryCodes, seed?.physicalHazardCategoryCodes);
  if (!untouched) return values;
  return {
    ...values,
    healthHazardCategoryCodes: undefined,
    physicalHazardCategoryCodes: undefined,
  };
}
